/**
 * SelectField — Radix Select bound to RHF.
 *
 * Driven by an `options` array of `{ value, label }`. For more elaborate item
 * markup, use `Select*` directly with `FormField` — this wrapper covers the
 * 90% case (a flat list of strings, e.g. Indian states on S03).
 */
import * as React from "react";
import { useFormContext, type FieldPath, type FieldValues } from "react-hook-form";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../primitives/select.js";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "./form.js";

export interface SelectOption {
  value: string;
  label: React.ReactNode;
}

export interface SelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  label: React.ReactNode;
  options: readonly SelectOption[];
  placeholder?: string;
  helper?: React.ReactNode;
  disabled?: boolean;
  /**
   * Label for an explicit "not set" item, e.g. `"Not specified"`. Radix forbids
   * a `SelectItem` with an empty value, so without this an optional field can
   * be set but never cleared. When provided, the item maps to `""` both ways.
   */
  noneLabel?: string;
}

/** Stands in for `""`, which Radix rejects as a `SelectItem` value. */
const NONE = "__none__";

export function SelectField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  options,
  placeholder = "Select…",
  helper,
  disabled,
  noneLabel,
}: SelectFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  return (
    <FormField<TFieldValues, TName>
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select
            // With `noneLabel`, an empty value selects the "none" item so the
            // trigger reads e.g. "Not specified" rather than the placeholder.
            // Without it, behaviour is unchanged: "" falls through to the
            // placeholder, exactly as every existing caller expects.
            value={noneLabel ? (field.value ? field.value : NONE) : (field.value ?? "")}
            onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
            {...(disabled === undefined ? {} : { disabled })}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {noneLabel ? <SelectItem value={NONE}>{noneLabel}</SelectItem> : null}
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {helper ? <FormDescription>{helper}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
